import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquare, FolderKanban, Mail, TrendingUp, Clock } from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const StatCard = ({ icon: Icon, label, value, color, bg }) => (
  <div data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white rounded-2xl border border-slate-100 p-6 flex items-start gap-4 shadow-sm">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bg}`}>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <div>
      <p className="text-3xl font-bold text-slate-800">{value}</p>
      <p className="text-sm text-slate-400 mt-0.5">{label}</p>
    </div>
  </div>
);

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/stats`, { headers: getHeaders() });
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
    setLoading(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="dashboard-home">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-sm text-slate-400 mt-1">Overview of your site activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard icon={MessageSquare} label="Total Messages" value={stats?.total_messages || 0} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={Mail} label="New Messages" value={stats?.new_messages || 0} color="text-amber-600" bg="bg-amber-50" />
        <StatCard icon={FolderKanban} label="Active Projects" value={stats?.active_projects || 0} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard icon={TrendingUp} label="Total Projects" value={stats?.total_projects || 0} color="text-purple-600" bg="bg-purple-50" />
      </div>

      {/* Recent Messages */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-50 flex items-center justify-between">
          <h2 className="font-semibold text-slate-700">Recent Messages</h2>
          <a href="/admin/messages" className="text-sm text-blue-500 hover:text-blue-700 transition-colors">View All</a>
        </div>
        <div className="divide-y divide-slate-50">
          {stats?.recent_messages?.length > 0 ? stats.recent_messages.map((msg, i) => (
            <div key={i} className="px-6 py-4 flex items-start gap-4 hover:bg-slate-50/50 transition-colors">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                msg.status === 'new' ? 'bg-blue-100 text-blue-600' : msg.status === 'contacted' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'
              }`}>
                {msg.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-slate-700">{msg.name}</span>
                  {msg.status === 'new' && <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold uppercase">New</span>}
                </div>
                <p className="text-xs text-slate-400">{msg.organization || msg.email}</p>
                <p className="text-sm text-slate-500 mt-1 truncate">{msg.message}</p>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-300 flex-shrink-0">
                <Clock className="w-3 h-3" />
                {msg.submitted_at ? format(new Date(msg.submitted_at), 'MMM d') : ''}
              </div>
            </div>
          )) : (
            <div className="px-6 py-12 text-center text-sm text-slate-300">No messages yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
